import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/outline';
import InfoTooltip from '../../../../components/InfoTooltip';
// import { CheckCircleIcon } from '@heroicons/react/solid';
import { getPatientFacingTerminology } from '../../../../utils/terminology';

export default function QuestionnaireAccordion({ 
  assessments, 
  patientDataModel, 
  nextQuestionnaireIndex,
  nextQuestionnaireRef,
  isInformant = false,
  userOrganisation = null,
  showInformantSetup,
  setShowInformantSetup,
  // Optional gate supplied by the caller: renders the same locked-forms
  // layout as the adult informant-setup gate but with caller-provided copy
  // and handlers. Used by the CYP ADHD guardian dashboard, where forms stay
  // locked until the school representative is nominated (or the guardian
  // skips with a reason). Shape: { title, body, ctaLabel, onCta,
  // secondaryLabel?, onSecondary? }.
  nominationGate = null
}) {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState(new Set()); // For main items (groups or individual)
  const t = getPatientFacingTerminology(userOrganisation);
  
  // Auto-expand the next questionnaire that needs attention
  React.useEffect(() => {
    if (nextQuestionnaireIndex >= 0) {
      setExpandedItems(new Set([nextQuestionnaireIndex]));
    } else {
      // If no specific next questionnaire (e.g., all complete), don't expand anything
      setExpandedItems(new Set());
    }
  }, [nextQuestionnaireIndex, assessments]);

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  // Helper function to format time estimate
  const formatTimeEstimate = (minutes) => {
    if (minutes < 1) return 'Less than 1 min';
    if (minutes === 1) return '1 min';
    return `${Math.round(minutes)} mins`;
  };

  // Helper function to calculate time estimate
  const getTimeEstimate = (assessment) => {
    
    // Determine time per question based on form type:
    // - BDEFS/WEISS: 90 seconds (complex multi-part questions with detailed scenarios)
    // - Rating scales: 10 seconds (simple Likert-style responses)
    // - History forms: 15 seconds (require more thought/recall than scales)
    const isBdefsOrWeiss = assessment.formKey === 'bdefs' || assessment.formKey === 'weiss';
    const isScale = assessment.type === 'scale';
    const secondsPerQuestion = isBdefsOrWeiss ? 90 : (isScale ? 10 : 15);
    
    // Known question counts for scales - used when progress.totalQuestions is not yet available
    // (i.e., before the user has started the form). These values match the form_data_model.py definitions.
    const knownQuestionCounts = {
      // Rating scales with many questions
      aq50: 50,           // Autism Spectrum Quotient (50-item)
      cnst: 60,           // Combined Neurodevelopmental Screening Tool (15+15+10+8+12)
      spat: 61,           // Sensory Profile Assessment Tool (8+8+8+8+8+7+7+7)
      catq: 25,           // Camouflaging Autistic Traits Questionnaire
      // ADHD symptom rating scales - Patient self-report
      asrs: 18,           // Adult ADHD Self-Report Scale
      baarsIVCurrent: 18, // BAARS-IV Current
      baarsIVChildhood: 18, // BAARS-IV Childhood
      // ADHD symptom rating scales - Informant report
      asrsOAdulthoodInformant: 18,    // ASRS-O Informant (observer version)
      snapIVChildhoodInformant: 18,   // SNAP-IV Childhood Informant
      snapIV26Informant: 26,          // SNAP-IV 26 (CYP ADHD informant)
      baarsIVCurrentInformant: 18,    // BAARS-IV Current Informant
      baarsIVChildhoodInformant: 18,  // BAARS-IV Childhood Informant
      // Shorter rating scales
      aq10: 10,           // Autism Spectrum Quotient (10-item)
      core10: 10,         // Clinical Outcomes in Routine Evaluation
      phq9: 9,            // Patient Health Questionnaire
      gad7: 7,            // Generalised Anxiety Disorder
      ess: 8,             // Epworth Sleepiness Scale
      auditc: 3,          // AUDIT-C (Alcohol Use)
      mdq: 15,            // Mood Disorder Questionnaire
      ybocs: 10,          // Yale-Brown Obsessive Compulsive Scale
    };
    
    const formData = patientDataModel?.formData?.[assessment.formKey];
    const progress = formData?.progress;
    
    // Get total question count: prefer saved progress, then known counts, then fallback
    let totalQuestions;
    if (progress?.totalQuestions) {
      totalQuestions = progress.totalQuestions;
    } else if (knownQuestionCounts[assessment.formKey]) {
      totalQuestions = knownQuestionCounts[assessment.formKey];
    } else {
      // Generic fallback for forms not in the lookup
      totalQuestions = assessment.type === 'scale' ? 10 : 25;
    }
    
    const answeredQuestions = progress?.answeredQuestions || 0;
    const remainingQuestions = Math.max(0, totalQuestions - answeredQuestions);
    
    // Calculate time based on form type
    const timeInMinutes = (remainingQuestions * secondsPerQuestion) / 60;
    
    return formatTimeEstimate(timeInMinutes);
  };

  // Determine the status and styling for each questionnaire (works for both groups and individuals)
  const getQuestionnaireStatus = (assessment, index, isChild = false, parentIndex = null) => {
    let timeEstimate;
    let progress;
    
    if (assessment.isGroup) {
      // For groups, calculate combined time estimate from incomplete children
      const incompleteChildren = assessment.children.filter(child => child.progress < 100);
      if (incompleteChildren.length === 0) {
        timeEstimate = 'Completed';
        progress = 100;
      } else {
        const totalMinutes = incompleteChildren.reduce((total, child) => {
          const childTimeString = getTimeEstimate(child);
          const childMinutes = parseFloat(childTimeString.replace(/[^\d.]/g, '')) || 0;
          return total + childMinutes;
        }, 0);
        timeEstimate = formatTimeEstimate(totalMinutes);
        progress = assessment.progress;
      }
    } else {
      timeEstimate = getTimeEstimate(assessment);
      progress = assessment.progress;
    }
    
    if (progress === 100) {
      return {
        status: 'completed',
        statusText: 'Completed',
        statusColor: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        timelineColor: 'bg-green-500'
      };
    } else if (progress > 0) {
      return {
        status: 'in-progress',
        statusText: `${timeEstimate} to complete`,
        statusColor: 'text-violet-600',
        bgColor: 'bg-violet-50',
        borderColor: 'border-violet-200',
        timelineColor: 'bg-violet-500'
      };
    } else {
      // Not started items should only be violet if they are the actual next item to work on
      // For groups: violet if it's the next group AND has incomplete children
      // For individual items: violet if it's specifically the next individual item
      const isNextInFocus = !isChild ? 
        (index === nextQuestionnaireIndex) : 
        false; // Children should never get violet just for being in the next group
        
      return {
        status: 'not-started',
        statusText: `est. ${timeEstimate}`,
        statusColor: isNextInFocus ? 'text-violet-600' : 'text-gray-500',
        bgColor: isNextInFocus ? 'bg-violet-50' : 'bg-gray-50',
        borderColor: isNextInFocus ? 'border-violet-200' : 'border-gray-200',
        timelineColor: isNextInFocus ? 'bg-violet-500' : 'bg-gray-300'
      };
    }
  };

  // Check if informant setup is needed first (skip for ThinkDivergent organization)
  const requiresInformantSetup = userOrganisation !== 'ThinkDivergent';
  const needsInformantSetup = requiresInformantSetup && !patientDataModel?.hasCompletedInformantSetupForm && !isInformant;

  // Resolve the gate to render: the adult informant-setup gate keeps its
  // exact existing copy and handler; otherwise a caller-supplied
  // nominationGate (CYP ADHD guardian school-rep nomination) uses the same
  // locked-forms layout with its own copy.
  const gate = needsInformantSetup
    ? {
        title: `Adding Your ${t.plural}`,
        body: patientDataModel?.assessmentModule === 'adultAutism'
          ? "To help with your assessment, we need information from someone who knows you well and can provide developmental history information."
          : "To help with your assessment, we need information from two people who know you well. This will help us understand your experiences at different stages of your life.",
        ctaLabel: 'Begin',
        onCta: () => setShowInformantSetup(true),
      }
    : nominationGate;

  if (gate) {
    return (
      <div ref={nextQuestionnaireIndex === -1 ? nextQuestionnaireRef : null}>
        {/* Simple informant setup card */}
        <div className="px-4 py-6 max-w-4xl mx-auto">
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            {/* Extension line to connect to questionnaires below */}
            <div className="absolute left-4 bottom-0 w-0.5 bg-gray-200 h-8 translate-y-full"></div>
            
            <div className="relative flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-violet-500 rounded-full border-4 border-white shadow-md flex items-center justify-center z-10">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              </div>
              
              <div className="ml-6 flex-1">
                <div className="bg-white border-2 border-violet-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="p-6">
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{gate.title}</h3>
                      <p className="text-left text-gray-600 text-sm">
                        {gate.body}
                      </p>
                    </div>

                    <div className="flex flex-col items-center gap-3">
                      <button
                        onClick={gate.onCta}
                        className="px-6 py-3 bg-violet-600 text-white font-semibold rounded-md hover:bg-violet-700 transition duration-200 shadow-sm"
                      >
                        {gate.ctaLabel}
                      </button>
                      {gate.secondaryLabel && (
                        <button
                          onClick={gate.onSecondary}
                          className="text-sm text-violet-700 underline hover:text-violet-900 transition duration-200"
                        >
                          {gate.secondaryLabel}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Preview of questionnaires below informant setup - greyed out and blurred */}
        <div className="relative mt-8">         
          
          {/* Greyed out questionnaires preview */}
          <div className="opacity-40 pointer-events-none filter blur-[1px]">
            <div className="px-4 pb-6 md:pb-12 max-w-4xl mx-auto">
              <div className="relative">
                {/* Extension line coming from informant setup above */}
                <div className="absolute left-4 top-0 w-0.5 bg-gray-200 h-6 -translate-y-full"></div>
                {/* Questionnaire items */}
                <div className="space-y-4">
                  {assessments.map((assessment, index) => {
                    const status = getQuestionnaireStatus(assessment, index);
                    const isLastItem = index === assessments.length - 1;
                    
                    return (
                      <div 
                        key={assessment.formKey || index} 
                        className="relative flex items-start"
                      >
                        {/* Timeline line segment - connects to next item */}
                        {!isLastItem && (
                          <div className="absolute left-4 top-8 w-0.5 bg-gray-200 z-0" 
                               style={{ height: 'calc(100% + 1rem)' }}>
                          </div>
                        )}

                        {/* Timeline dot with radial progress */}
                        <div className="flex-shrink-0 w-8 h-8 relative z-10">
                          {/* Background circle */}
                          <div className="w-8 h-8 rounded-full bg-gray-200 border-4 border-white shadow-md"></div>
                          
                          {/* Center icon */}
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                            <span className="w-2 h-2 bg-current rounded-full"></span>
                          </div>
                        </div>
                        
                        {/* Card content */}
                        <div className="ml-6 flex-1">
                          <div className={`${status.bgColor} border-2 ${status.borderColor} shadow-sm rounded-lg overflow-hidden`}>
                            {/* Header - Always visible */}
                            <div className="w-full p-4 text-left">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                    {assessment.title}
                                  </h3>                          
                                  <div className="flex items-center space-x-4">                            
                                    <span className="text-sm font-medium text-gray-500">
                                      est. {getTimeEstimate(assessment)}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Expand/Collapse icon */}
                                <div className="ml-4">
                                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-6 md:pb-12 max-w-4xl mx-auto">
      <div className="relative">
        {/* Questionnaire items */}
        <div className="space-y-4">
          {assessments.map((assessment, index) => {
            const isExpanded = expandedItems.has(index);
            const status = getQuestionnaireStatus(assessment, index);
            const isLastItem = index === assessments.length - 1;
            
            return (
              <div 
                key={assessment.formKey || index} 
                ref={index === nextQuestionnaireIndex ? nextQuestionnaireRef : null}
                className="relative flex items-start"
              >
                {/* Timeline line segment - connects to next item */}
                {!isLastItem && (
                  <div className="absolute left-4 top-8 w-0.5 bg-gray-200 z-0" 
                       style={{ height: 'calc(100% + 1rem)' }}>
                  </div>
                )}

                {/* Timeline dot with radial progress */}
                <div className="flex-shrink-0 w-8 h-8 relative z-10">
                  {/* Background circle */}
                  <div className="w-8 h-8 rounded-full bg-gray-200 border-4 border-white shadow-md"></div>
                  
                  {/* Progress circle */}
                  {assessment.progress > 0 && (
                    <svg className="absolute inset-0 w-8 h-8 transform -rotate-90">
                      <circle
                        cx="16"
                        cy="16"
                        r="12"
                        stroke={assessment.progress === 100 ? '#10b981' : '#8b5cf6'}
                        strokeWidth="4"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 12}`}
                        strokeDashoffset={`${2 * Math.PI * 12 * (1 - assessment.progress / 100)}`}
                        className="transition-all duration-500 ease-out"
                      />
                    </svg>
                  )}
                  
                  {/* Center icon */}
                  <div className={`absolute inset-0 flex items-center justify-center ${
                    assessment.progress === 100 ? 'text-green-600' : 
                    assessment.progress > 0 ? 'text-violet-600' : 'text-gray-400'
                  }`}>
                    {assessment.progress === 100 ? (
                      <CheckIcon className="w-4 h-4" />
                    ) : (
                      <span className="w-2 h-2 bg-current rounded-full"></span>
                    )}
                  </div>
                </div>
                
                {/* Card content */}
                <div className="ml-6 flex-1">
                  <div className={`${status.bgColor} ${
                    index === nextQuestionnaireIndex && assessment.progress < 100 && !assessment.isGroup
                      ? 'border-2 border-violet-400 shadow-lg shadow-violet-100 animate-gentle-pulse'
                      : `border-2 ${status.borderColor} shadow-sm`
                  } rounded-lg overflow-hidden transition-all duration-200`}>
                    {/* Header - Always visible */}
                    <button
                      onClick={() => toggleExpanded(index)}
                      aria-expanded={isExpanded}
                      aria-controls={`accordion-content-${assessment.formKey || index}`}
                      className={`w-full p-4 text-left transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-inset ${
                        status.status === 'completed' 
                          ? 'hover:bg-green-100' 
                          : status.status === 'in-progress' || (status.status === 'not-started' && index === nextQuestionnaireIndex)
                            ? 'hover:bg-violet-100'
                            : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {assessment.title}
                          </h3>                          
                          <div className="flex items-center space-x-4">                            
                            {/* {assessment.progress > 0 && (
                              <div className="flex-1 max-w-xs">
                                                                 <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                   <div
                                     className={`h-full transition-all duration-300 ${
                                       assessment.progress === 100 ? 'bg-green-500' : 'bg-violet-500'
                                     }`}
                                     style={{ width: `${assessment.progress}%` }}
                                   />
                                 </div>
                              </div>
                            )} */}
                            <span className={`text-sm font-medium ${status.statusColor}`}>
                              {status.statusText}
                            </span>
                          </div>
                        </div>
                        
                        {/* Expand/Collapse icon */}
                        <div className="ml-4">
                          {isExpanded ? (
                            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </button>
                    
                    {/* Expanded content with smooth animation */}
                    <div
                      id={`accordion-content-${assessment.formKey || index}`}
                      role="region"
                      aria-label={assessment.title}
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      {assessment.isGroup ? (
                        /* Group content - show children */
                        <div className="border-t border-gray-100 bg-white">
                          <div className="p-2">
                            {/* <p className="text-gray-600 text-sm text-left mb-4">
                              {assessment.subtitle}
                            </p> */}
                            
                            {/* {assessment.progress === 100 && (
                              <div className="flex items-center mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                <span className="text-green-800 font-medium">
                                  All questionnaires in this group have been completed
                                </span>
                              </div>
                            )} */}
                          </div>
                          
                          {/* Child questionnaires - Compact layout */}
                          <div className="space-y-2 px-4 pb-4">
                            {assessment.children.map((child, childIndex) => {
                              const childStatus = getQuestionnaireStatus(child, childIndex, true, index);
                              // Find the first incomplete child in this group to determine which one is "next up"
                              const firstIncompleteChildIndex = assessment.children.findIndex(c => c.progress < 100);
                              const isNextUpChild = index === nextQuestionnaireIndex && childIndex === firstIncompleteChildIndex;
                              
                              return (
                                <div 
                                  key={child.formKey}
                                  className={`${childStatus.bgColor} ${
                                    isNextUpChild && child.progress === 0
                                      ? 'border-2 border-violet-400 shadow-md shadow-violet-100 animate-gentle-pulse'
                                      : `border ${childStatus.borderColor} shadow-sm`
                                  } rounded-lg p-3 transition-all duration-200`}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    {/* Left side: Title, status, and tooltip */}
                                    <div className="flex-1 min-w-0 text-left">
                                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                                        {child.title}
                                      </h4>                        
                                      <div className="flex items-center gap-1">
                                        {child.subtitle && (
                                          <InfoTooltip 
                                            content={child.subtitle}
                                            label={`Information about ${child.title}`}
                                            side="top"
                                            maxWidth="300px"
                                            className="flex-shrink-0 inline-flex items-center"
                                          />
                                        )}
                                        <span className={`text-xs font-medium leading-none ${childStatus.statusColor}`}>
                                          {childStatus.statusText}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Right side: CTA button */}
                                    <div className="flex-shrink-0">
                                      <button 
                                        className={`py-1.5 px-3 rounded-md font-semibold transition duration-200 shadow-sm text-xs whitespace-nowrap ${
                                          child.progress === 100 
                                            ? 'bg-white text-green-600 border border-green-500 hover:bg-green-50'
                                            : child.progress > 0 || isNextUpChild
                                              ? 'bg-violet-600 text-white hover:bg-violet-700'
                                              : 'bg-white text-violet-600 border border-violet-500 hover:bg-violet-50'
                                        }`}
                                        aria-label={`${child.progress === 100 ? 'Review' : child.progress > 0 ? 'Continue' : 'Start'} ${child.title}`}
                                        onClick={() => {              
                                          navigate(`${child.route}`);                
                                        }}
                                      >
                                        {child.progress === 100 
                                          ? 'Review' 
                                          : child.progress > 0 
                                            ? 'Continue' 
                                            : 'Start'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        /* Individual questionnaire content */
                        <div className="border-t border-gray-100 p-4 bg-white">
                          <div className="flex flex-col md:flex-row gap-4">
                            {/* Content */}
                            <div className="flex-1">
                              <p className="text-gray-600 mb-4 text-sm text-left">
                                {assessment.subtitle}
                              </p>
                              
                              {/* {assessment.progress === 100 && (
                                <div className="flex items-center mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                  <span className="text-green-800 font-medium">
                                    This questionnaire has been completed
                                  </span>
                                </div>
                              )} */}
                              
                              {/* Action button */}
                              <button 
                                className={`w-full py-3 px-4 rounded-lg font-semibold transition duration-200 shadow-sm ${
                                  assessment.progress === 100 
                                    ? 'bg-white text-green-600 border-2 border-green-500 hover:bg-green-50'
                                    : assessment.progress > 0 || (assessment.progress === 0 && index === nextQuestionnaireIndex)
                                      ? 'bg-violet-600 text-white hover:bg-violet-700'
                                      : 'bg-white text-violet-600 border-2 border-violet-500 hover:bg-violet-50'
                                }`}
                                aria-label={`${assessment.progress === 100 ? 'Review and edit answers for' : assessment.progress > 0 ? 'Continue' : 'Start'} ${assessment.title}`}
                                onClick={() => {              
                                  navigate(`${assessment.route}`);                
                                }}
                              >
                                {assessment.progress === 100 
                                  ? 'Review & Edit Answers' 
                                  : assessment.progress > 0 
                                    ? 'Continue' 
                                    : 'Start'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
} 